/**
 * CSV Export Button Component
 * 
 * A reusable dropdown button for CSV export functionality.
 * Provides options to export all rows or only selected rows.
 */

import React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import IconProvider from "@/components/ui/IconProvider";

export interface CsvExportButtonProps {
  /** Callback when "Export All" is clicked */
  onExportAll: () => void;
  /** Callback when "Export Selected" is clicked */
  onExportSelected?: () => void;
  /** Number of selected items (shows "Export Selected" option when > 0) */
  selectedCount?: number;
  /** Button variant */
  variant?: "default" | "outline" | "ghost" | "link" | "destructive" | "secondary";
  /** Button size */
  size?: "default" | "sm" | "lg" | "icon";
  /** Whether to show text label */
  showLabel?: boolean;
  /** Custom label text */
  label?: string;
  /** Custom class name */
  className?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Tooltip/title text */
  title?: string;
}

/**
 * A dropdown button component for CSV export actions.
 * 
 * @example
 * // Basic usage with icon only
 * <CsvExportButton
 *   onExportAll={() => handleExportAll()}
 *   onExportSelected={() => handleExportSelected()}
 *   selectedCount={5}
 * />
 * 
 * @example
 * // With label
 * <CsvExportButton
 *   onExportAll={() => handleExportAll()}
 *   showLabel
 *   label="Export All"
 *   variant="outline"
 * />
 */
export function CsvExportButton({
  onExportAll,
  onExportSelected,
  selectedCount = 0,
  variant = "ghost",
  size = "sm",
  showLabel = false,
  label = "Export",
  className = "",
  disabled = false,
  title = "Export to CSV",
}: CsvExportButtonProps) {
  const hasSelection = selectedCount > 0 && onExportSelected;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={showLabel ? `gap-2 ${className}` : `h-8 w-8 p-0 ${className}`}
          disabled={disabled}
          title={title}
        >
          <IconProvider iconstring="fa-download" size={14} />
          {showLabel && label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onExportAll}>
          <IconProvider iconstring="fa-file-csv" size={14} className="mr-2" />
          Export All Rows
        </DropdownMenuItem>
        {hasSelection && (
          <DropdownMenuItem onClick={onExportSelected}>
            <IconProvider iconstring="fa-check-square" size={14} className="mr-2" />
            Export Selected ({selectedCount})
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default CsvExportButton;
